// src/app/core/subscription/utils/subscription-date.utils.ts

/**
 * Estrutura mínima compatível com Firestore Timestamp.
 */
interface FirestoreTimestampLike {
  toDate(): Date;
}

/**
 * Converte diferentes representações de data para Date.
 *
 * Aceita:
 * - Date;
 * - Firestore Timestamp;
 * - string válida;
 * - número válido em milissegundos.
 *
 * Retorna null quando o valor está ausente ou é inválido.
 */
export function normalizeSubscriptionDate(value: unknown): Date | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    return isValidDate(value) ? new Date(value.getTime()) : null;
  }

  if (isFirestoreTimestampLike(value)) {
    try {
      const convertedDate = value.toDate();

      return isValidDate(convertedDate)
        ? new Date(convertedDate.getTime())
        : null;
    } catch {
      return null;
    }
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const convertedDate = new Date(value);

    return isValidDate(convertedDate) ? convertedDate : null;
  }

  return null;
}

function isFirestoreTimestampLike(
  value: unknown
): value is FirestoreTimestampLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as FirestoreTimestampLike).toDate === 'function'
  );
}

function isValidDate(value: Date): boolean {
  return !Number.isNaN(value.getTime());
}
